/**
 * Admin System Settings Page
 * Configure RSVP cutoff rules
 */

import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';
import { fetchSystemSettings, updateSystemSettings } from '@/lib/api/systemSettings';
import { DAY_LABELS } from '@/lib/utils/menuHelpers';

const dayOptions = DAY_LABELS.map((label, value) => ({
  value: String(value),
  label,
}));

export function SystemSettings() {
  const { user } = useAuthStore();
  const { showToast } = useUIStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cutoffDay, setCutoffDay] = useState('2');
  const [cutoffTime, setCutoffTime] = useState('23:59');

  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        const response = await fetchSystemSettings();
        setCutoffDay(String(response.cutoff.cutoffDay));
        setCutoffTime(response.cutoff.cutoffTime);
      } catch (error) {
        showToast(
          error instanceof Error ? error.message : 'Failed to load settings',
          'error'
        );
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [showToast]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateSystemSettings({
        cutoffDay: Number.parseInt(cutoffDay, 10),
        cutoffTime,
      });
      showToast('System settings updated', 'success');
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to update settings',
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  if (user?.role !== 'ADMIN') {
    return (
      <div className="container mx-auto p-8 max-w-6xl">
        <Card className="p-12 text-center">
          <p className="text-muted-foreground">
            You do not have permission to access this page.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-8 max-w-4xl">
      <Card>
        <CardHeader>
          <CardTitle>System Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="cutoffDay">RSVP cutoff day</Label>
                <Select value={cutoffDay} onValueChange={setCutoffDay}>
                  <SelectTrigger id="cutoffDay">
                    <SelectValue placeholder="Select day" />
                  </SelectTrigger>
                  <SelectContent>
                    {dayOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="cutoffTime">RSVP cutoff time</Label>
                <Input
                  id="cutoffTime"
                  type="time"
                  value={cutoffTime}
                  onChange={(event) => setCutoffTime(event.target.value)}
                />
                <p className="text-sm text-muted-foreground">Timezone is fixed to Pacific.</p>
              </div>

              <div className="flex justify-end">
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
